// frontend/src/pages/manufacturing/StockConversion.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  Inventory as InventoryIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import debounce from 'lodash/debounce';
import usePageTitle from '../../hooks/usePageTitle';

const StockConversion = () => {
  // Source item state
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceItem, setSourceItem] = useState(null);
  const [sourceQuantity, setSourceQuantity] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);

  // Destination item state
  const [destSearch, setDestSearch] = useState('');
  const [destOptions, setDestOptions] = useState([]);
  const [destItem, setDestItem] = useState(null);
  const [destQuantity, setDestQuantity] = useState('');
  const [destLoading, setDestLoading] = useState(false);

  // Form state
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastConversion, setLastConversion] = useState(null);

  usePageTitle('Stock Conversion');

  // Debounced search for source items
  const searchSourceItems = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSourceOptions([]);
        return;
      }
      setSourceLoading(true);
      try {
        const response = await manufacturingAPI.items.list({ search: query, page_size: 20 });
        setSourceOptions(response.data.results || []);
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setSourceLoading(false);
      }
    }, 300),
    []
  );

  // Debounced search for destination items
  const searchDestItems = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setDestOptions([]);
        return;
      }
      setDestLoading(true);
      try {
        const response = await manufacturingAPI.items.list({ search: query, page_size: 20 });
        setDestOptions(response.data.results || []);
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setDestLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    searchSourceItems(sourceSearch);
  }, [sourceSearch, searchSourceItems]);

  useEffect(() => {
    searchDestItems(destSearch);
  }, [destSearch, searchDestItems]);

  // Get stock level for selected source item
  const [sourceStock, setSourceStock] = useState(null);
  useEffect(() => {
    if (sourceItem) {
      manufacturingAPI.stockLevels.list({ search: sourceItem.item_code })
        .then(response => {
          const stock = response.data.results?.find(s => s.item === sourceItem.id);
          setSourceStock(stock);
        })
        .catch(() => setSourceStock(null));
    } else {
      setSourceStock(null);
    }
  }, [sourceItem]);

  const handleSubmit = async () => {
    // Validation
    if (!sourceItem) {
      toast.error('Please select a source item');
      return;
    }
    if (!sourceQuantity || parseFloat(sourceQuantity) <= 0) {
      toast.error('Please enter a valid source quantity');
      return;
    }
    if (!destItem) {
      toast.error('Please select a destination item');
      return;
    }
    if (!destQuantity || parseFloat(destQuantity) <= 0) {
      toast.error('Please enter a valid destination quantity');
      return;
    }
    if (sourceItem.id === destItem.id) {
      toast.error('Source and destination items must be different');
      return;
    }

    setSubmitting(true);
    try {
      const response = await manufacturingAPI.stockConversion.convert({
        source_item_id: sourceItem.id,
        source_quantity: parseFloat(sourceQuantity),
        destination_item_id: destItem.id,
        destination_quantity: parseFloat(destQuantity),
        notes: notes,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setLastConversion(response.data);
        
        // Reset form
        setSourceItem(null);
        setSourceQuantity('');
        setSourceSearch('');
        setDestItem(null);
        setDestQuantity('');
        setDestSearch('');
        setNotes('');
        setSourceStock(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Conversion failed';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSourceItem(null);
    setSourceQuantity('');
    setSourceSearch('');
    setDestItem(null);
    setDestQuantity('');
    setDestSearch('');
    setNotes('');
    setSourceStock(null);
    setLastConversion(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapIcon fontSize="large" />
        Stock Conversion
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Convert stock from one item to another. This will consume the source item and create the destination item.
      </Typography>

      {lastConversion && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setLastConversion(null)}
          icon={<CheckIcon />}
        >
          <Typography variant="subtitle2">
            Conversion {lastConversion.conversion_ref} completed
          </Typography>
          <Typography variant="body2">
            {lastConversion.stock_levels?.source?.item}: now {lastConversion.stock_levels?.source?.new_quantity} | 
            {lastConversion.stock_levels?.destination?.item}: now {lastConversion.stock_levels?.destination?.new_quantity}
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Source Section */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                  Consume (Source)
                </Typography>

                <Autocomplete
                  options={sourceOptions}
                  getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                  value={sourceItem}
                  onChange={(_, newValue) => setSourceItem(newValue)}
                  inputValue={sourceSearch}
                  onInputChange={(_, newValue) => setSourceSearch(newValue)}
                  loading={sourceLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Item"
                      placeholder="Type to search..."
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {sourceLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                {sourceItem && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Selected: <strong>{sourceItem.item_code}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sourceItem.name}
                    </Typography>
                    {sourceStock && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <InventoryIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Available: <strong>{parseFloat(sourceStock.quantity_on_hand).toFixed(2)}</strong> {sourceItem.unit_of_measure}
                      </Typography>
                    )}
                  </Box>
                )}

                <TextField
                  label="Quantity to Consume"
                  type="number"
                  value={sourceQuantity}
                  onChange={(e) => setSourceQuantity(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: sourceItem ? (
                      <InputAdornment position="end">{sourceItem.unit_of_measure}</InputAdornment>
                    ) : null,
                  }}
                  inputProps={{ min: 0, step: 'any' }}
                  error={sourceStock && parseFloat(sourceQuantity) > parseFloat(sourceStock.quantity_on_hand)}
                  helperText={
                    sourceStock && parseFloat(sourceQuantity) > parseFloat(sourceStock.quantity_on_hand)
                      ? 'Exceeds available stock'
                      : ''
                  }
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Arrow */}
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SwapIcon sx={{ fontSize: 48, color: 'primary.main', transform: { xs: 'rotate(90deg)', md: 'rotate(0deg)' } }} />
          </Grid>

          {/* Destination Section */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                  Create (Destination)
                </Typography>

                <Autocomplete
                  options={destOptions}
                  getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                  value={destItem}
                  onChange={(_, newValue) => setDestItem(newValue)}
                  inputValue={destSearch}
                  onInputChange={(_, newValue) => setDestSearch(newValue)}
                  loading={destLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Item"
                      placeholder="Type to search..."
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {destLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                {destItem && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Selected: <strong>{destItem.item_code}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {destItem.name}
                    </Typography>
                  </Box>
                )}

                <TextField
                  label="Quantity to Create"
                  type="number"
                  value={destQuantity}
                  onChange={(e) => setDestQuantity(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: destItem ? (
                      <InputAdornment position="end">{destItem.unit_of_measure}</InputAdornment>
                    ) : null,
                  }}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g., Cutting panel in half, splitting bulk material..."
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={submitting}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !sourceItem || !destItem || !sourceQuantity || !destQuantity}
                startIcon={submitting ? <CircularProgress size={20} /> : <SwapIcon />}
              >
                {submitting ? 'Converting...' : 'Convert Stock'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default StockConversion;